import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
	getPreferredContractInfo,
	createContractFromOffer,
	fetchContractList,
	fetchContractById,
	cancelContract,
} from '../services/api'
import { useToken } from '../utils/auth'

/**
 * 사용자의 선호 계약 정보를 조회하는 훅
 */
export const usePreferredContract = () => {
	const token = useToken()

	return useQuery({
		queryKey: ['preferred-contract'],
		queryFn: () => getPreferredContractInfo(token),
		enabled: !!token,
		retry: 1,
		select: (data) => {
			if (data == null) {
				return {
					domesticContract: null,
					overseasContract: null,
					hasNoPreferredContract: true,
					needsOnboarding: true,
				}
			}
			// 원본 데이터 + 파생된 유틸리티 필드들
			const hasNoPreferredContract =
				data.domesticContract === null || data.overseasContract === null

			return {
				...data, // 원본 데이터 (domesticContract, overseasContract)
				hasNoPreferredContract, // 선호계약정보가 없으면 true
				needsOnboarding: hasNoPreferredContract, // 온보딩이 필요한지 여부
			}
		},
	})
}

/**
 * 오퍼로부터 계약을 생성하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useCreateContractFromOffer = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, offerId }) => createContractFromOffer(token, offerId),
		onSuccess: (data, variables) => {
			console.log('계약 생성 성공:', data)
			// 오퍼 목록 쿼리도 무효화
			queryClient.invalidateQueries({ queryKey: ['offer-list'] })
			queryClient.invalidateQueries({ queryKey: ['offerDetail', variables.offerId] })
			queryClient.invalidateQueries({ queryKey: ['contract-list'] })
			queryClient.invalidateQueries({ queryKey: ['contractDetail', data.id] })
		},
		onError: (error) => {
			console.error('계약 생성 실패:', error)
		},
	})
}

/**
 * 계약 목록 조회 훅
 * @returns {Object} { data, isLoading, isError, error, refetch }
 */
export const useContractList = () => {
	const token = useToken()

	return useQuery({
		queryKey: ['contract-list'],
		queryFn: () => fetchContractList(token),
		enabled: !!token,
		retry: 1,
	})
}

/**
 * 계약 상세 조회 훅
 * @param {string} contractId - 계약 ID
 * @returns {Object} { data, isLoading, isError, error, refetch }
 */
export const useContractDetail = (contractId) => {
	const token = useToken()

	return useQuery({
		queryKey: ['contractDetail', contractId],
		queryFn: () => fetchContractById(contractId, token),
		enabled: !!contractId && !!token,
		retry: 1,
	})
}

/**
 * 계약 취소/거절 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useCancelContract = () => {
	const queryClient = useQueryClient()
	const token = useToken()

	return useMutation({
		mutationFn: ({ contractId, by }) => cancelContract(token, contractId, by),
		onSuccess: (data, variables) => {
			console.log('계약 취소/거절 성공:', data)
			// 계약 상세 조회 쿼리 무효화
			queryClient.invalidateQueries({
				queryKey: ['contractDetail', variables.contractId],
			})
			// 계약 목록 쿼리도 무효화
			queryClient.invalidateQueries({ queryKey: ['contract-list'] })
		},
		onError: (error) => {
			console.error('계약 취소/거절 실패:', error)
		},
	})
}
